import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, rufe } from "../api/client";
import type { OffeneZahlung, VerkaufZeile } from "../../worker/data/verkaeufe";
import type { CsvSpalten } from "../../worker/sumup/csv";
import type { HubStatus } from "../../worker/durable/LadenHub";

export type { OffeneZahlung, VerkaufZeile, CsvSpalten };

export interface ErstattungZeile {
  id: string;
  transactionCode: string;
  betragCent: number;
  zahlungsart: "bar" | "karte" | "sonstig";
  zeitpunkt: string;
  typ: "zahlung" | "erstattung" | "rueckbuchung";
  status: string;
  warenkorbNummer: number | null;
  rueckgabeId: string | null;
}

export interface OffeneRueckgabe {
  id: string;
  code: string;
  bezeichnung: string;
  betragCent: number;
  erstelltAt: string;
  grund: string;
}

export interface VerkaeufeAntwort {
  tag: string;
  verkaeufe: VerkaufZeile[];
  nichtZugeordnet: OffeneZahlung[];
  erstattungen: { transaktionen: ErstattungZeile[]; offeneRueckgaben: OffeneRueckgabe[] };
  hub: HubStatus;
}

export const KEY_VERKAEUFE = ["laden", "verkaeufe"] as const;

export function useVerkaeufe(tag: string) {
  return useQuery({ queryKey: [...KEY_VERKAEUFE, tag], queryFn: () => rufe<VerkaeufeAntwort>(api.api.laden.verkaeufe.$get({ query: { tag } })), refetchInterval: 30_000 });
}

function verkaufsMutation<TVars>(fn: (v: TVars) => Promise<Response>) {
  return () => {
    const qc = useQueryClient();
    return useMutation({ mutationFn: (v: TVars) => rufe<{ ok: true }>(fn(v)), onSuccess: () => void qc.invalidateQueries({ queryKey: KEY_VERKAEUFE }) });
  };
}

export const useZuordnen = verkaufsMutation((v: { transactionCode: string; warenkorbId: string }) => api.api.laden.verkaeufe.zuordnen.$post({ json: v }));
export const useAlsLadenware = verkaufsMutation((v: { transactionCode: string; bezeichnung: string }) => api.api.laden.verkaeufe.ladenware.$post({ json: v }));
export const useErstattungVerknuepfen = verkaufsMutation((v: { transactionCode: string; rueckgabeId: string }) => api.api.laden.verkaeufe.erstattung.$post({ json: v }));
export const useVerkaufAbbrechen = verkaufsMutation((v: { id: string; grund: string }) => api.api.laden.verkaeufe[":id"].abbrechen.$post({ param: { id: v.id }, json: { grund: v.grund } }));

// --- CSV-Import ----------------------------------------------------------------------------------

export interface CsvAnalyseAntwort {
  trennzeichen: string;
  spalten: string[];
  vorschlag: CsvSpalten;
  anzahl: number;
  beispiel: Record<string, string>[];
}

export interface CsvVorschauZeile {
  zeile: number;
  zeitpunkt: string;
  betragCent: number;
  zahlungsart: "bar" | "karte" | "sonstig";
  status: string;
  typ: string;
  transactionCode: string;
  fehler: string | null;
  ergebnis: "zugeordnet" | "neu" | "doppelt" | "fehler";
  warenkorbNummer: number | null;
}

export interface CsvVorschauAntwort {
  zeilen: CsvVorschauZeile[];
  zusammenfassung: { gesamt: number; zugeordnet: number; neu: number; doppelt: number; fehler: number };
}

export function useCsvAnalyse() {
  return useMutation({ mutationFn: (v: { text: string; dateiname: string }) => rufe<CsvAnalyseAntwort>(api.api.laden["csv-import"].analyse.$post({ json: v })) });
}

export function useCsvVorschau() {
  return useMutation({ mutationFn: (v: { text: string; spalten: CsvSpalten }) => rufe<CsvVorschauAntwort>(api.api.laden["csv-import"].vorschau.$post({ json: v })) });
}

export function useCsvImport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { text: string; spalten: CsvSpalten; dateiname: string }) => rufe<{ ok: true; neu: number; uebersprungen: number; zugeordnet: number }>(api.api.laden["csv-import"].$post({ json: v })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["laden"] }),
  });
}

export interface CsvImportEintrag {
  id: string;
  dateiname: string;
  zeilen: number;
  neu: number;
  zugeordnet: number;
  uebersprungen: number;
  erstelltAt: string;
}

export function useCsvVerlauf() {
  return useQuery({ queryKey: ["laden", "csv-import", "verlauf"], queryFn: () => rufe<{ importe: CsvImportEintrag[] }>(api.api.laden["csv-import"].verlauf.$get()) });
}

// --- Ladenware-Kacheln ---------------------------------------------------------------------------

export interface KachelEintrag {
  id: string;
  name: string;
  preisCent: number | null;
  icon: string;
  reihenfolge: number;
  aktiv: boolean;
}

export function useKachelnVerwaltung() {
  return useQuery({ queryKey: ["laden", "ladenware"], queryFn: () => rufe<{ kacheln: KachelEintrag[] }>(api.api.laden.ladenware.$get()) });
}

export function useKachelAnlegen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { name: string; preisCent: number | null; icon: string; reihenfolge: number }) => rufe<{ id: string }>(api.api.laden.ladenware.$post({ json: v })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["laden", "ladenware"] }),
  });
}

export function useKachelAendern() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; name: string; preisCent: number | null; icon: string; reihenfolge: number; aktiv: boolean }) => rufe(api.api.laden.ladenware[":id"].$put({ param: { id: v.id }, json: { name: v.name, preisCent: v.preisCent, icon: v.icon, reihenfolge: v.reihenfolge, aktiv: v.aktiv } })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["laden", "ladenware"] }),
  });
}
